import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import FeedbackRenderer from 'components/InstructorViewSubmission/FeedbackRenderer';
import Header from 'components/InstructorViewSubmission/Header';
import { QuizReviewContext } from 'components/QuizGrading/QuizReviewContext';
import ResponseViewer from 'components/QuizGrading/ResponseViewer';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Container, Icon, IconButton, Stack, Text } from 'shell/components';

const ViewQuizSubmissionLayout = ({ device, ...props }) => {
    const { t } = useTranslation('assignment');
    const {
        currentAssignment: quiz,
        currentSubmission,
        form,
        setForm,
        tab,
        setTab,
        selectedCriterionId,
        setSelectedCriterionId,
        selectedFeedbackId,
        setSelectedFeedbackId,
        selectedFileId,
        setSelectedFileId,
        highlights,
        gradeOverrides,
        setGradeOverrides,
        isEditing,
        isSaving,
        isCurrentFormValid,
        onClickEdit,
        onClickEditSave,
        onClickEditCancel,
        updateSubmission,
        handleCriterionNavigation,
    } = props;
    const desktop = device === 'desktop';
    const mobile = device === 'mobile';
    const saveFeedback = (update) => updateSubmission(typeof update === 'function' ? update(form) : update);

    useEffect(() => setSelectedFileId(''), [selectedCriterionId, selectedFeedbackId, tab, setSelectedFileId]);

    return (
        <QuizReviewContext.Provider
            value={{ questions: quiz.questions, selectedCriterionId, onSelectQuestion: setSelectedFileId }}
        >
            <Container
                maxWidth={false}
                sx={(theme) => ({
                    position: 'absolute',
                    inset: 0,
                    zIndex: theme.zIndex.drawer + 1,
                    p: 0,
                    pt: mobile ? '129px' : '81px',
                    height: '100%',
                    minHeight: 0,
                    background: theme.palette.ensightful.neutral[100],
                })}
            >
                <Header />
                <Stack direction={desktop ? 'row' : 'column'} sx={{ height: '100%', minHeight: 0 }}>
                    <Stack
                        component={'section'}
                        aria-label={t('quiz:submission.responses', 'Quiz responses')}
                        sx={{
                            width: desktop ? '55%' : '100%',
                            height: desktop ? '100%' : '40%',
                            minHeight: 0,
                            flexShrink: 0,
                            overflow: 'auto',
                        }}
                    >
                        <ResponseViewer
                            quiz={quiz}
                            submission={currentSubmission}
                            selectedCriterionId={selectedCriterionId}
                            selectedFeedbackId={selectedFeedbackId}
                            selectedQuestionId={selectedFileId}
                            feedback={form.feedback}
                            tab={tab}
                        />
                    </Stack>
                    <Stack
                        component={'section'}
                        aria-label={t('quiz:submission.confirmed-results', 'Confirmed grades and feedback')}
                        aria-busy={isSaving}
                        sx={(theme) => ({
                            width: desktop ? '45%' : '100%',
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            borderLeft: desktop ? `1px solid ${theme.palette.ensightful.neutral[300]}` : undefined,
                            borderTop: desktop ? undefined : `1px solid ${theme.palette.ensightful.neutral[300]}`,
                            background: theme.palette.ensightful.shades[0],
                        })}
                    >
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            spacing={2}
                            sx={{ p: mobile ? 3 : 5, flexShrink: 0, flexWrap: 'wrap', rowGap: 2 }}
                        >
                            {isEditing ? (
                                <Stack direction={'row'} spacing={2} sx={{ ml: 'auto' }}>
                                    <Button
                                        disabled={isSaving}
                                        color={'neutral'}
                                        variant={'tertiary'}
                                        onClick={onClickEditCancel}
                                    >
                                        {t('common:controls.cancel', 'Cancel')}
                                    </Button>
                                    <Button
                                        disabled={!isCurrentFormValid || isSaving}
                                        loading={isSaving}
                                        onClick={onClickEditSave}
                                    >
                                        {t('common:controls.save', 'Save')}
                                    </Button>
                                </Stack>
                            ) : (
                                <>
                                    <ButtonGroup
                                        maxWidth={mobile ? 'calc(100% - 40px)' : '240px'}
                                        sx={{
                                            flexShrink: 0,
                                            '& .MuiButton-root': {
                                                fontSize: (theme) => `${theme.typography.button.fontSize.medium}rem`,
                                            },
                                        }}
                                        options={[
                                            { key: 'grades', value: t('common:ui.grades', 'Grades') },
                                            { key: 'feedback', value: t('common:ui.feedback', 'Feedback') },
                                        ]}
                                        selected={tab}
                                        onChange={setTab}
                                    />
                                    {tab === 'grades' &&
                                        (mobile ? (
                                            <IconButton
                                                aria-label={t('grading-section.edit-grades', 'Edit Grades')}
                                                disabled={isSaving}
                                                kind={'edit'}
                                                size={'small'}
                                                variant={'square'}
                                                onClick={onClickEdit}
                                            />
                                        ) : (
                                            <Button
                                                disabled={isSaving}
                                                color={'neutral'}
                                                variant={'tertiary'}
                                                startIcon={<Icon kind={'edit'} />}
                                                onClick={onClickEdit}
                                            >
                                                {t('grading-section.edit-grades', 'Edit Grades')}
                                            </Button>
                                        ))}
                                </>
                            )}
                        </Stack>
                        <Stack
                            component={'fieldset'}
                            disabled={isSaving}
                            sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', border: 0, p: 0, m: 0 }}
                        >
                            {tab === 'grades' && (
                                <RubricRenderer
                                    assignment={quiz}
                                    currentSubmission={currentSubmission}
                                    disabled={!isEditing || isSaving}
                                    showGraded={false}
                                    showGradedTime={false}
                                    showGradingExplanationInput={isEditing}
                                    showSuggestedTag={false}
                                    form={form}
                                    setForm={setForm}
                                    gradeOverrides={gradeOverrides}
                                    setGradeOverrides={setGradeOverrides}
                                    selectedCriterionId={selectedCriterionId}
                                    setSelectedCriterionId={setSelectedCriterionId}
                                    selectedFileId={selectedFileId}
                                    setSelectedFileId={setSelectedFileId}
                                    locations={highlights}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                    hideCriterionNavigation={isEditing || !form.feedback}
                                />
                            )}
                            {tab === 'feedback' &&
                                (!form.feedback ? (
                                    <Text color={'light'} sx={{ p: 5 }}>
                                        {t(
                                            'quiz:submission.no-feedback',
                                            'No feedback was confirmed for this submission.'
                                        )}
                                    </Text>
                                ) : typeof form.feedback === 'string' ? (
                                    <LegacyFeedbackRenderer
                                        currentSubmission={currentSubmission}
                                        form={form}
                                        setForm={saveFeedback}
                                    />
                                ) : (
                                    <FeedbackRenderer
                                        assignment={quiz}
                                        currentSubmission={currentSubmission}
                                        selectedFileId={selectedFileId}
                                        setSelectedFileId={setSelectedFileId}
                                        locations={highlights}
                                        form={form}
                                        setForm={saveFeedback}
                                        selectedFeedbackId={selectedFeedbackId}
                                        setSelectedFeedbackId={setSelectedFeedbackId}
                                        handleCriterionNavigation={handleCriterionNavigation}
                                    />
                                ))}
                        </Stack>
                    </Stack>
                </Stack>
            </Container>
        </QuizReviewContext.Provider>
    );
};

export default ViewQuizSubmissionLayout;
